import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Request from "common/Request";
import { expressDestination, expressCategory } from "./common";
import { expressLatencyTarget, parseLatencyDistributions } from "../api";
import MultiBarChart from "common/graphs/MultiBarChart";
import NumberInputWithSpinner from "common/NumberInputWithSpinnerState";
import provideUserFile from "common/ProvideFile";
import { YYYYMMDDHHmmssSSS } from "common/utils";
import { safeStr } from "common/api";

const DPI_STATS_TMOUT_MS = 3600000;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: visible;
`;

const Header = styled.h4`
  margin: 10px 0 0 0 !important;
`;

const HeaderWrapper = styled.div`
  margin-bottom: 10px;
  position: relative;
`;

const LabelWithIcon = styled.div`
  span {
    font-weight: bold;
  }
  white-space: nowrap;
  cursor: pointer;
  display: inline;
  color: #333;
  .material-icons {
    line-height: 1.2em;
    vertical-align: middle;
  }
`;

const asCSVContent = (input) =>
  input
    .split("\n")
    .map((line) => line.replaceAll(/\s+/g, ","))
    .join("\n");

const createCVSFilename = (prefix = "dpi-latencies") =>
  `${prefix}-${YYYYMMDDHHmmssSSS(new Date())}.csv`;

const ExportButton = ({ csv, className = "" }) => {
  return (
    <LabelWithIcon
      className={`${className} export-dpi-latencies`}
      onClick={() => provideUserFile(createCVSFilename(), asCSVContent(csv))}
    >
      <span>Export:</span>
      <i className="material-icons">file_download</i>
    </LabelWithIcon>
  );
};

function getCmdSubscribergroup(group) {
  if(!group || group === "all-subscribers") return "";
  return `subscriber-groups name '${safeStr(group).replaceAll("'", "")}'`;
}

const parseCategoriesLatencies = (response, latencies) => {
  const {latenciesMap, latenciesOrder} = latencies.reduce((acc, latency, index) => {
    acc.latenciesMap[latency.service] = latency.latency;
    acc.latenciesOrder[latency.service] = index;
    return acc;
  }, {latenciesMap: {}, latenciesOrder: {}});
  const [header, ...rows] = response.trim().split("\n");
  const headerAttrs = header.split(/\s+/);
  const headerMap = headerAttrs.reduce((acc, attr, index) => {
    acc[index] = attr;
    return acc;
  }, {});
  const data = rows.reduce((acc, row) => {
    const rowAttrs = row.split(/\s+/);
    let category;
    const values = rowAttrs.reduce((accRow, attr, index) => {
      if (headerMap[index] !== "CATEGORY") {
        accRow.push({ name: headerMap[index], value: parseFloat(attr) });
      } else {
        category = attr;
      }
      return accRow;
    }, []);
    acc.push({
      name: category,
      values,
      meanValue:
        category === "all"
          ? latenciesMap["all-average"]
          : latenciesMap[category],
    });
    return acc;
  }, []);
  const categories = headerAttrs.slice(1);
  const orderData = data.sort((a, b) => latenciesOrder[a.name] - latenciesOrder[b.name]);
  return { data: orderData, categories, csv: response };
};

const loadDistribution = ({
  latencies,
  hours,
  target,
  destination,
  filter,
  dpiEnabled,
  interval,
  subscriberGroup,
}) => {
  return expressLatencyTarget(target ?? { addr: null, subsId: null })
    .then((expressedTarget) => {
      const csvList = latencies
        .filter(({ service }) => service !== "all-average")
        .map(({ service }) => `"${safeStr(service).replaceAll("'", "")}"`)
        .join(",");
      return ifCl.run(
        `show statistics dpi latency bins category '${csvList}' \
         hours ${hours} bins 21 bin-size 5 minimum 5 \
        ${expressedTarget} ${expressDestination(destination)} \
        ${filter === "auto" ? "udrs-auto" : ""} \
        ${getCmdSubscribergroup(subscriberGroup)}`,
        { timeout: DPI_STATS_TMOUT_MS }
      );
    })
    .then((response) => parseCategoriesLatencies(response, latencies))
    .catch((error) => showModalError("Error:", error));
};

function LatencyAndDistribution({
  latencies,
  hours,
  target,
  destination,
  filter,
  dpiEnabled,
  interval,
  onSelect,
  subscriberGroup,
}) {
  const [request, setRequest] = useState(null);
  const doLoad = () => {
    setRequest(
      loadDistribution({
        latencies,
        hours,
        target,
        destination,
        filter,
        dpiEnabled,
        interval,
        subscriberGroup,
      })
    );
  };

  useEffect(
    () => doLoad(),
    [
      JSON.stringify(latencies),
    ]
  );
  return (
    <Request during={request}>
      {(result) => {
        const { data, categories, csv } = result;
        return (
          <Wrapper>
            <HeaderWrapper>
              <Header>Distribution of Internet Latencies</Header>
              <ExportButton className="align-right link" csv={csv} />
            </HeaderWrapper>
            <div className="latency-charts-wrapper">
              <MultiBarChart
                data={data}
                categories={categories}
                onSelect={onSelect}
                overlap={0.8}
                meanLabel="MEAN (ms)"
                yAxisLabel="APPLICATION"
                yAxisUnit="%"
              />
            </div>
          </Wrapper>
        );
      }}
    </Request>
  );
}

export default LatencyAndDistribution;
